import React, { useState, useEffect } from "react";
import { Create, useForm } from "@refinedev/antd";
import { Form, Input, DatePicker, Button, Table, Select, InputNumber, Alert, Space, Divider, Typography } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { ICreateJournalEntry, ICreateGeneralLedgerEntry, IChartOfAccount } from "../../../interfaces/accounting";
import dayjs from "dayjs";

const { Text } = Typography;

export const JournalEntriesCreate: React.FC = () => {
    const { formProps, saveButtonProps } = useForm();
    const [entries, setEntries] = useState<ICreateGeneralLedgerEntry[]>([
        { accountCode: "", debitAmount: 0, creditAmount: 0, description: "" },
        { accountCode: "", debitAmount: 0, creditAmount: 0, description: "" }
    ]);

    // Fetch chart of accounts for selection
    const { data: accountsData } = useList({
        resource: "chartofaccounts",
        pagination: { mode: "off" }
    });

    const accounts = (accountsData?.data || []) as IChartOfAccount[];
    const activeAccounts = accounts.filter(acc => acc.isActive);

    // Calculate totals
    const totalDebits = entries.reduce((sum, entry) => sum + (entry.debitAmount || 0), 0);
    const totalCredits = entries.reduce((sum, entry) => sum + (entry.creditAmount || 0), 0);
    const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

    const addEntry = () => {
        setEntries([...entries, { accountCode: "", debitAmount: 0, creditAmount: 0, description: "" }]);
    };

    const removeEntry = (index: number) => {
        if (entries.length > 2) {
            const newEntries = entries.filter((_, i) => i !== index);
            setEntries(newEntries);
        }
    };

    const updateEntry = (index: number, field: keyof ICreateGeneralLedgerEntry, value: any) => {
        const newEntries = [...entries];
        newEntries[index] = { ...newEntries[index], [field]: value };
        
        // If updating debit amount, clear credit amount and vice versa
        if (field === 'debitAmount' && value > 0) {
            newEntries[index].creditAmount = 0;
        } else if (field === 'creditAmount' && value > 0) {
            newEntries[index].debitAmount = 0;
        }
        
        setEntries(newEntries);
    };

    // Update form when entries change
    useEffect(() => {
        formProps.form?.setFieldValue('entries', entries);
    }, [entries, formProps.form]);

    const columns = [
        {
            title: "Account",
            dataIndex: "accountCode",
            render: (value: string, record: ICreateGeneralLedgerEntry, index: number) => (
                <Select
                    value={value}
                    onChange={(val) => updateEntry(index, 'accountCode', val)}
                    placeholder="Select account"
                    style={{ width: '100%', minWidth: 200 }}
                    showSearch
                    filterOption={(input, option) =>
                        (option?.children as string)?.toLowerCase().includes(input.toLowerCase()) ||
                        option?.value?.toString().toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {activeAccounts.map(account => (
                        <Select.Option key={account.accountCode} value={account.accountCode}>
                            {account.accountCode} - {account.accountName}
                        </Select.Option>
                    ))}
                </Select>
            ),
            width: 250,
        },
        {
            title: "Description",
            dataIndex: "description",
            render: (value: string, record: ICreateGeneralLedgerEntry, index: number) => (
                <Input
                    value={value}
                    onChange={(e) => updateEntry(index, 'description', e.target.value)}
                    placeholder="Entry description"
                />
            ),
        },
        {
            title: "Debit",
            dataIndex: "debitAmount",
            render: (value: number, record: ICreateGeneralLedgerEntry, index: number) => (
                <InputNumber
                    value={value}
                    onChange={(val) => updateEntry(index, 'debitAmount', val || 0)}
                    precision={2}
                    min={0}
                    style={{ width: '100%' }}
                    formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) : 0}
                />
            ),
            width: 150,
        },
        {
            title: "Credit",
            dataIndex: "creditAmount",
            render: (value: number, record: ICreateGeneralLedgerEntry, index: number) => (
                <InputNumber
                    value={value}
                    onChange={(val) => updateEntry(index, 'creditAmount', val || 0)}
                    precision={2}
                    min={0}
                    style={{ width: '100%' }}
                    formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) : 0}
                />
            ),
            width: 150,
        },
        {
            title: "Action",
            render: (value: any, record: ICreateGeneralLedgerEntry, index: number) => (
                <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => removeEntry(index)}
                    disabled={entries.length <= 2}
                />
            ),
            width: 80,
        },
    ];

    return (
        <Create 
            saveButtonProps={{
                ...saveButtonProps,
                disabled: !isBalanced || entries.length < 2 || entries.some(e => !e.accountCode || !e.description)
            }}
        >
            <Alert
                message="Journal Entry"
                description="Create a new journal entry. Ensure debits equal credits before saving."
                type="info"
                style={{ marginBottom: 24 }}
            />

            <Form 
                {...formProps} 
                layout="vertical"
                onFinish={(values) => {
                    // Add entries to form values
                    const journalEntry: ICreateJournalEntry = {
                        ...values,
                        entries: entries.filter(entry => 
                            entry.accountCode && 
                            entry.description && 
                            (entry.debitAmount > 0 || entry.creditAmount > 0)
                        )
                    };
                    formProps.onFinish?.(journalEntry);
                }}
            >
                <Form.Item
                    label="Transaction Date"
                    name="transactionDate"
                    initialValue={dayjs()}
                    rules={[{ required: true, message: "Transaction date is required" }]}
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                    label="Description"
                    name="description"
                    rules={[
                        { required: true, message: "Description is required" },
                        { min: 5, max: 500, message: "Description must be 5-500 characters" }
                    ]}
                >
                    <Input placeholder="e.g., Payment received from customer" />
                </Form.Item>

                <Form.Item
                    label="Reference (Optional)"
                    name="reference"
                >
                    <Input placeholder="e.g., Invoice #INV-001, Check #123" />
                </Form.Item>

                <Divider>Journal Entry Lines</Divider>

                <Table
                    dataSource={entries}
                    columns={columns}
                    pagination={false}
                    rowKey={(record, index) => index!}
                    scroll={{ x: 800 }}
                />

                <Space style={{ marginTop: 16, marginBottom: 16 }}>
                    <Button type="dashed" onClick={addEntry} icon={<PlusOutlined />}>
                        Add Line
                    </Button>
                </Space>

                <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space size="large">
                            <Text strong>Total Debits: ${totalDebits.toLocaleString()}</Text>
                            <Text strong>Total Credits: ${totalCredits.toLocaleString()}</Text>
                            <Text strong style={{ color: isBalanced ? 'green' : 'red' }}>
                                Difference: ${Math.abs(totalDebits - totalCredits).toLocaleString()}
                            </Text>
                        </Space>
                        
                        {!isBalanced && (
                            <Alert
                                message="Journal Entry Not Balanced"
                                description="Debits must equal credits in a valid journal entry."
                                type="error"
                                showIcon
                            />
                        )}
                    </Space>
                </div>

                <Form.Item
                    label="Notes (Optional)"
                    name="notes"
                    style={{ marginTop: 16 }}
                >
                    <Input.TextArea 
                        rows={3} 
                        placeholder="Additional notes for this journal entry"
                        maxLength={1000}
                        showCount
                    />
                </Form.Item>

                {/* Hidden field for entries */}
                <Form.Item name="entries" hidden>
                    <Input />
                </Form.Item>
            </Form>
        </Create>
    );
};